import type { HTMLAttributes, ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function VFTh({
  children,
  onClick,
  sortBy,
  sortDirection,
  col,
  align = 'left',
  onHide,
  dragProps,
}: {
  children: ReactNode;
  onClick?: () => void;
  /** La colonne sur laquelle le tableau est trie, pour savoir si c'est celle-ci. */
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  /** La colonne que cette cellule represente. */
  col?: string;
  align?: 'left' | 'center' | 'right';
  onHide?: () => void;
  /** Les attributs que le gestionnaire de glisser-deposer pose sur la cellule. */
  dragProps?: HTMLAttributes<HTMLTableCellElement>;
}) {
  const { t } = useTranslation(['dialer', 'common']);
  const active = sortBy === col;
  // K3 — la colonne triee montre son sens et l'annonce (aria-sort) ; les
  // autres gardent l'icone neutre.
  const SortIcon = !active ? ArrowUpDown : sortDirection === 'asc' ? ArrowUp : ArrowDown;
  const hideLabel = t('dialer:contacts.columnsPanel.hideColumn');
  return (
    <th
      {...(dragProps || {})}
      onClick={onClick}
      onKeyDown={onClick ? (e) => {
        if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        onClick();
      } : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-sort={onClick ? (active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
      className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider cursor-pointer select-none outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-vf-accent"
      style={{
        color: active ? 'var(--vf-text-1)' : 'var(--vf-text-3)',
        textAlign: align,
      }}
    >
      <div className={`group/th inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        {/* Correction #42 — croix de masquage sur l'en-tete. Elle n'apparait
            qu'au survol pour ne pas encombrer, et double le panneau Colonnes :
            masquer se fait la ou on regarde, pas dans un menu. */}
        {onHide && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onHide(); }}
            title={hideLabel}
            aria-label={hideLabel}
            className="opacity-0 group-hover/th:opacity-100 transition-opacity shrink-0 leading-none"
            style={{ color: 'var(--vf-text-4)', fontSize: 12, padding: '0 2px' }}
          >
            ×
          </button>
        )}
        <SortIcon className={`w-3 h-3 ${active ? 'opacity-90' : 'opacity-40'}`} aria-hidden />
        {children}
      </div>
    </th>
  );
}

// Palette unifiée — 4 familles sémantiques + violet pour IA/RDV.
// Tons doux (50 bg / 700 text / 200 border) — alignés avec InlineCallStatus
// et PostCallScore pour rester cohérent dans toute l'app.
//   • slate   → neutre / passé (terminé, sans réponse, annulé, do_not_call)
//   • sky     → info / en cours (numérotation, sonnerie, contacté)
//   • emerald → succès (en ligne, intéressé, qualifié)
//   • amber   → attention (occupé, rappel demandé)
//   • rose    → échec / négatif (pas intéressé, échec, injoignable, mauvais num)
//   • violet  → IA / RDV / messagerie (états spéciaux)
// Palettes distinctes pour eviter qu'un prospect "Nouveau" et "Contacté"
// aient la meme couleur. Conventions :
//   • indigo  → vraiment neuf (jamais appele)
//   • cyan    → a recontacter (pending mais last_call_at set)
//   • blue    → contacte (humain decroche, pas de qualif finale)
//   • green   → succes (en ligne, qualifie, interesse)
